<template>
    <div class="member-level">
        <div class="page-header">
            <div>
                <h2 class="page-title">会员等级</h2>
                <p class="page-desc">设置会员等级和权益规则</p>
            </div>
            <el-button type="primary" size="large" @click="openAddDialog">
                <el-icon>
                    <Plus />
                </el-icon>
                新增等级
            </el-button>
        </div>

        <div class="level-cards">
            <div class="level-card" v-for="level in levels" :key="level.level_code">
                <div class="level-icon" :class="level.level_code">
                    <el-icon :size="40">
                        <Medal v-if="level.level_code === 'bronze'" />
                        <Trophy v-else-if="level.level_code === 'silver'" />
                        <StarFilled v-else-if="level.level_code === 'gold'" />
                        <Star v-else-if="level.level_code === 'platinum'" />
                        <Coin v-else-if="level.level_code === 'diamond'" />
                        <GoldMedal v-else />
                    </el-icon>
                </div>
                <h3 class="level-name">{{ level.level_name }}</h3>
                <div class="level-condition">
                    <el-icon>
                        <Coin />
                    </el-icon> 所需积分: {{ level.min_points }}-{{ level.max_points === 999999 ? '∞' : level.max_points }}
                </div>
                <div class="level-discount">
                    <el-icon>
                        <Discount />
                    </el-icon> 折扣: {{ (level.discount_rate * 10).toFixed(1) }}折
                </div>
                <div class="level-growth">
                    <el-icon>
                        <TrendCharts />
                    </el-icon> 成长系数: {{ level.growth_factor }}x
                </div>
                <div class="level-benefits">
                    <div class="benefits-title">会员权益</div>
                    <div class="benefits-list">
                        <div v-for="(benefit, idx) in level.benefits" :key="idx" class="benefit-item">
                            <el-icon>
                                <Check />
                            </el-icon> {{ benefit }}
                        </div>
                    </div>
                </div>
                <div class="level-actions">
                    <el-button type="primary" link size="small" @click="openEditDialog(level)">
                        <el-icon>
                            <Edit />
                        </el-icon> 编辑
                    </el-button>
                    <el-popconfirm title="确定删除该等级吗？" @confirm="handleDelete(level.id)">
                        <template #reference>
                            <el-button type="danger" link size="small">
                                <el-icon>
                                    <Delete />
                                </el-icon> 删除
                            </el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </div>

            <div v-if="levels.length === 0 && !loading" class="empty-state">
                <el-empty description="暂无会员等级，点击右上角新增">
                    <el-button type="primary" @click="openAddDialog">新增等级</el-button>
                </el-empty>
            </div>
        </div>

        <!-- 新增/编辑弹窗 -->
        <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑会员等级' : '新增会员等级'" width="600px" destroy-on-close>
            <el-form :model="formData" :rules="formRules" ref="formRef" label-width="110px">
                <el-form-item label="等级名称" prop="level_name" required>
                    <el-input v-model="formData.level_name" placeholder="请输入等级名称，如：普通会员" />
                </el-form-item>

                <el-form-item label="等级代码" prop="level_code" required>
                    <el-select v-model="formData.level_code" placeholder="请选择等级代码" style="width: 100%">
                        <el-option label="普通会员 (bronze)" value="bronze" />
                        <el-option label="银卡会员 (silver)" value="silver" />
                        <el-option label="金卡会员 (gold)" value="gold" />
                        <el-option label="白金会员 (platinum)" value="platinum" />
                        <el-option label="钻石会员 (diamond)" value="diamond" />
                        <el-option label="黑金会员 (black)" value="black" />
                    </el-select>
                    <div class="form-tip">等级代码用于区分等级，不可重复</div>
                </el-form-item>

                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="所需最低积分" prop="min_points">
                            <el-input-number v-model="formData.min_points" :min="0" :step="100" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="所需最高积分" prop="max_points">
                            <el-input-number v-model="formData.max_points" :min="0" :step="100" style="width: 100%" />
                            <div class="form-tip">0或999999表示不限制</div>
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="折扣率" prop="discount_rate">
                            <el-input-number v-model="formData.discount_rate" :min="0" :max="1" :precision="2"
                                :step="0.01" style="width: 100%" />
                            <div class="form-tip">例：0.95表示95折</div>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="成长系数" prop="growth_factor">
                            <el-input-number v-model="formData.growth_factor" :min="0" :max="3" :precision="2"
                                :step="0.1" style="width: 100%" />
                            <div class="form-tip">消费积分获取倍数</div>
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-form-item label="会员权益">
                    <div v-for="(benefit, index) in formData.benefits" :key="index" class="benefit-input">
                        <el-input v-model="formData.benefits[index]" placeholder="请输入权益描述"
                            style="width: calc(100% - 60px)" />
                        <el-button type="danger" circle size="small" @click="removeBenefit(index)">
                          <el-icon><Delete /></el-icon>
                        </el-button>
                    </div>
                    <el-button type="primary" link size="small" @click="addBenefit">
                        <el-icon>
                            <Plus />
                        </el-icon> 添加权益
                    </el-button>
                </el-form-item>

                <el-form-item label="状态">
                    <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="启用"
                        inactive-text="禁用" />
                </el-form-item>

                <el-form-item label="排序">
                    <el-input-number v-model="formData.sort_order" :min="0" :max="999" style="width: 100%" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { 
  Medal, Trophy, Star, StarFilled, GoldMedal,
  Coin, Discount, TrendCharts, Check, Plus, Edit, Delete 
} from '@element-plus/icons-vue'

export default {
    name: 'MemberLevelPage',
    components: {
        Medal, Trophy, Star, StarFilled, GoldMedal,
        Coin, Discount, TrendCharts, Check, Plus, Edit, Delete
    },
    setup() {
        const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
        const agentId = ref(userInfo.agent_id || 0)
        const storeId = ref(userInfo.id)

        const loading = ref(false)
        const submitting = ref(false)
        const levels = ref([])
        const dialogVisible = ref(false)
        const isEditMode = ref(false)
        const formRef = ref(null)

        const formData = reactive({
            id: null,
            level_name: '',
            level_code: '',
            min_points: 0,
            max_points: 0,
            discount_rate: 1.00,
            growth_factor: 1.00,
            benefits: [],
            status: 1,
            sort_order: 0
        })

        const formRules = {
            level_name: [{ required: true, message: '请输入等级名称', trigger: 'blur' }],
            level_code: [{ required: true, message: '请选择等级代码', trigger: 'change' }]
        }

        // 获取等级列表
        const fetchLevels = async () => {
            loading.value = true
            try {
                const response = await axios.get(`${config.BASE_URL}/shop/api/member_api.php`, {
                    params: { action: 'levels', agent_id: agentId.value, store_id: storeId.value }
                })
                if (response.data.code === 200) {
                    levels.value = response.data.data.map(level => ({
                        ...level,
                        benefits: level.benefits ? (typeof level.benefits === 'string' ? JSON.parse(level.benefits) : level.benefits) : []
                    }))
                }
            } catch (error) {
                console.error('获取等级失败:', error)
                ElMessage.error('获取数据失败')
            } finally {
                loading.value = false
            }
        }

        // 重置表单
        const resetForm = () => {
            Object.assign(formData, {
                id: null,
                level_name: '',
                level_code: '',
                min_points: 0,
                max_points: 0,
                discount_rate: 1.00,
                growth_factor: 1.00,
                benefits: [],
                status: 1,
                sort_order: 0
            })
        }

        // 添加权益
        const addBenefit = () => {
            formData.benefits.push('')
        }

        // 移除权益
        const removeBenefit = (index) => {
            formData.benefits.splice(index, 1)
        }

        // 打开新增弹窗
        const openAddDialog = () => {
            isEditMode.value = false
            resetForm()
            dialogVisible.value = true
        }

        // 打开编辑弹窗
        const openEditDialog = (level) => {
            isEditMode.value = true
            Object.assign(formData, {
                id: level.id,
                level_name: level.level_name,
                level_code: level.level_code,
                min_points: level.min_points,
                max_points: level.max_points,
                discount_rate: parseFloat(level.discount_rate),
                growth_factor: parseFloat(level.growth_factor),
                benefits: level.benefits ? [...level.benefits] : [],
                status: level.status,
                sort_order: level.sort_order
            })
            dialogVisible.value = true
        }

        // 提交表单
        const submitForm = async () => {
            if (!formRef.value) return

            await formRef.value.validate(async (valid) => {
                if (!valid) return

                submitting.value = true
                const loadingInstance = ElLoading.service({ text: '保存中...' })

                try {
                    const payload = {
                        action: isEditMode.value ? 'update_level' : 'create_level',
                        agent_id: agentId.value,
                        store_id: storeId.value,
                        ...formData,
                        benefits: JSON.stringify(formData.benefits.filter(b => b.trim()))
                    }

                    const response = await axios.post(`${config.BASE_URL}/shop/api/member_api.php`, payload)

                    if (response.data.code === 200) {
                        ElMessage.success(isEditMode.value ? '更新成功' : '创建成功')
                        dialogVisible.value = false
                        fetchLevels()
                    } else {
                        ElMessage.error(response.data.message || '操作失败')
                    }
                } catch (error) {
                    console.error('提交失败:', error)
                    ElMessage.error('操作失败')
                } finally {
                    submitting.value = false
                    loadingInstance.close()
                }
            })
        }

        // 删除等级
        const handleDelete = async (id) => {
            const loadingInstance = ElLoading.service({ text: '删除中...' })
            try {
                const response = await axios.delete(`${config.BASE_URL}/shop/api/member_api.php`, {
                    data: { action: 'delete_level', id, store_id: storeId.value }
                })
                if (response.data.code === 200) {
                    ElMessage.success('删除成功')
                    fetchLevels()
                } else {
                    ElMessage.error(response.data.message || '删除失败')
                }
            } catch (error) {
                console.error('删除失败:', error)
                ElMessage.error('删除失败')
            } finally {
                loadingInstance.close()
            }
        }

        onMounted(() => {
            fetchLevels()
        })

        return {
            loading,
            submitting,
            levels,
            dialogVisible,
            isEditMode,
            formRef,
            formData,
            formRules,
            addBenefit,
            removeBenefit,
            openAddDialog,
            openEditDialog,
            submitForm,
            handleDelete,
            fetchLevels
        }
    }
}
</script>

<style scoped lang="scss">
.member-level {
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .page-title {
            font-size: 24px;
            font-weight: 600;
            color: #1a1a2e;
            margin-bottom: 4px;
        }

        .page-desc {
            font-size: 14px;
            color: #8c8c8c;
        }
    }

    .level-cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 24px;

        .level-card {
            background: #fff;
            border-radius: 20px;
            padding: 24px;
            text-align: center;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            transition: all 0.3s;
            position: relative;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
            }

            .level-icon {
                width: 80px;
                height: 80px;
                margin: 0 auto 16px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;

                &.bronze {
                    background: linear-gradient(135deg, #cd7f32, #b8860b);
                    color: #fff;
                }

                &.silver {
                    background: linear-gradient(135deg, #c0c0c0, #a8a8a8);
                    color: #fff;
                }

                &.gold {
                    background: linear-gradient(135deg, #ffd700, #ffb800);
                    color: #fff;
                }

                &.platinum {
                    background: linear-gradient(135deg, #e5e4e2, #b8b8b8);
                    color: #fff;
                }

                &.diamond {
                    background: linear-gradient(135deg, #00b4d8, #0077b6);
                    color: #fff;
                }

                &.black {
                    background: linear-gradient(135deg, #2d2d2d, #1a1a1a);
                    color: #fff;
                }
            }

            .level-name {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 16px;
                color: #1a1a2e;
            }

            .level-condition,
            .level-discount,
            .level-growth {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                font-size: 14px;
                color: #666;
                margin-bottom: 10px;

                .el-icon {
                    font-size: 16px;
                }
            }

            .level-benefits {
                margin-top: 20px;
                padding-top: 16px;
                border-top: 1px solid #f0f0f0;

                .benefits-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a1a2e;
                    margin-bottom: 12px;
                }

                .benefits-list {
                    text-align: left;

                    .benefit-item {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 13px;
                        color: #666;
                        margin-bottom: 8px;

                        .el-icon {
                            color: #67c23a;
                            font-size: 14px;
                        }
                    }
                }
            }

            .level-actions {
                margin-top: 16px;
                padding-top: 12px;
                border-top: 1px solid #f0f0f0;
                display: flex;
                justify-content: center;
                gap: 16px;
            }
        }
    }

    .empty-state {
        grid-column: 1 / -1;
        padding: 60px 0;
    }

    .benefit-input {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
    }

    .form-tip {
        font-size: 12px;
        color: #8c8c8c;
        margin-top: 4px;
    }
}

@media (max-width: 768px) {
    .member-level .level-cards {
        grid-template-columns: 1fr;
    }
}
</style>